import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { OTPVerficationPage } from './OTP/OTPVerficationPage'
import { triggerRebalanceQuestionnaireOTP, verifyRebalanceQuestionnaireOTP } from '../repository/rebalance'
import { RebalanceQuestionnaireOTPVerifyPayload, RebalanceQuestionnaireOTPVerifyResponse } from '../types/rebalance'

const OTP_VERIFICATION_LIMIT = 3

export const RebalanceQuestionnaireOTPPage: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isOTPVerified, setIsOTPVerified] = useState(false)
  const [isOTPVerificationLoading, setIsOTPVerificationLoading] = useState(false)

  const [otpVerificationCount, setOtpVerificationCount] = useState(0)
  const [searchParams] = useSearchParams()
  const familyMemberPhoneNumber = searchParams?.get('phoneNumber') || ''
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | undefined>()

  const from = searchParams?.get('from') || ''

  // Trigger OTP on mount
  const { mutate: triggerOTPMutate } = useMutation({
    mutationFn: triggerRebalanceQuestionnaireOTP,
    onSuccess: (data) => {
      setOtpVerificationCount(0)
    },
    onError: (error: any) => {
      console.error('Failed to trigger OTP:', error)
    }
  })

  // Verify OTP mutation
  const { mutate: verifyOTPMutate } = useMutation({
    mutationFn: verifyRebalanceQuestionnaireOTP,
    onSuccess: (data: RebalanceQuestionnaireOTPVerifyResponse) => {
      if (data?.verified) {
        setIsOTPVerified(true)
        // Remove queries from cache to force fresh fetch from backend
        queryClient?.removeQueries({ queryKey: ['smartRebalance'] })
        queryClient?.removeQueries({ queryKey: ['SMART_REBALANCE'] })
        queryClient?.removeQueries({ queryKey: ['rebalance-plan-summary'] })
        // Navigate to questionnaire on success
        navigate(`/rebalance-questionnaire${from ? `?from=${from}` : ''}`, { replace: true })
      } else {
        setOtpErrorMessage(data?.message || 'Invalid OTP')
      }
      setIsOTPVerificationLoading(false)
    },
    onError: (error: any) => {
      console.error('OTP verification failed:', error)
      const statusCode = error?.response?.status
      if (statusCode === 400 || statusCode === 401) {
      } else if (statusCode === 410) {
      } else {
      }
      setIsOTPVerificationLoading(false)
    }
  })

  // Trigger OTP on component mount
  useEffect(() => {
    triggerOTPMutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isOTPValid = (otp: string) => {
    return otp.length === 6 && /^\d{6}$/.test(otp)
  }

  const handleOTPVerification = async (otp: string) => {
    if (!isOTPValid(otp)) {
      return
    }

    if (otpVerificationCount >= OTP_VERIFICATION_LIMIT) {
      return
    }

    setIsOTPVerificationLoading(true)
    setOtpVerificationCount(otpVerificationCount + 1)

    const payload: RebalanceQuestionnaireOTPVerifyPayload = {
      postBackParams: {
        familyConsentType: 'REBALANCE_RISK_PROFILE'
      },
      inputData: {
        authCode: otp
      }
    }

    verifyOTPMutate(payload)
  }

  const handleResendOTP = () => {
    triggerOTPMutate()
    setOtpVerificationCount(0)
  }

  return (
    <OTPVerficationPage
      numberOfDigits={6}
      phoneNumber={familyMemberPhoneNumber || ''}
      isOTPVerified={isOTPVerified}
      OTPVerificationFailedCase={null}
      isOTPVerificationLoading={isOTPVerificationLoading}
      isOTPVerificationLimitExceeded={false}
      handleResendOTP={handleResendOTP}
      handleOTPVerification={handleOTPVerification}
      inputContainerTestLocator='REBALANCE_QUESTIONNAIRE_ENTER_OTP'
      otpErrorMessage={otpErrorMessage}
    />
  )
}
